import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import useHandleErrors from '../../utils/user.utils';
import AddPartnerModal from './AddPartnerModal';
import ViewPartnerModal from './ViewPartnerModal';
import EditPartnerModal from './EditPartnerModal';
import { saveAs } from 'file-saver';
import { utils, write } from 'xlsx';

const Partners = () => {
    const [partners, setPartners] = useState([]);
    const [filteredPartners, setFilteredPartners] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [recordsPerPage, setRecordsPerPage] = useState(10);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedPartner, setSelectedPartner] = useState(null);
    const [loading, setLoading] = useState(false);
    
    const storedToken = localStorage.getItem("token");
    const handleErrors = useHandleErrors();
    const hasFetchedRef = useRef(false);

    useEffect(() => {
        const preloading = document.getElementById("preloading");
        setTimeout(() => {
            if (preloading) {
                preloading.style.display = "none";
            }
        }, 400);
    }, []);

     const fetchPartners = async () => {
    setLoading(true);
    try {
        const path = "/getpartnervendoronDetails";
        const URL = process.env.REACT_APP_BE_URL + path;
        const response = await axios.get(URL, {
            headers: {
                Authorization: `Bearer ${storedToken}`,
            },
        });
        if (response.status === 200) {
            // Filter only only vendors
            const all = response.data.partnerVendorDetails || [];
            const Partners = all.filter(item => item.UserType === "Partner");
            setPartners(Partners);
            setFilteredPartners(Partners);
        } else {
            handleErrors(response);
        }
    } catch (error) {
        if (error.response) {
            handleErrors(error.response);
        } else {
            console.error("Error fetching partners:", error);
            toast.error("Failed to fetch partners");
        }
    } finally {
        setLoading(false);
    }
};

    useEffect(() => {
        if (!hasFetchedRef.current) {
            fetchPartners();
            hasFetchedRef.current = true;
        }
    }, []);

    useEffect(() => {
        setFilteredPartners(
            partners.filter(partner =>
                partner.PartnerName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                partner.RegistrationNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                partner.PartnerType?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                partner.Email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                partner.PartnerManager?.toLowerCase().includes(searchQuery.toLowerCase())
            )
        );
        setCurrentPage(1);
    }, [searchQuery, partners]);

    const handleAddPartner = async (partnerData) => {
        try {
            const path = "/partnervendoronboard";
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.post(URL, partnerData, {
                headers: {
                    Authorization: `Bearer ${storedToken}`,
                    'Content-Type': 'multipart/form-data',
                },
            });
            if (response.status === 201 || response.status === 200) {
                toast.success("Partner Onboarded successfully");
                fetchPartners();
                setShowAddModal(false);
            } else {
                handleErrors(response);
            }
        } catch (error) {
            if (error.response) {
                handleErrors(error.response);
            } else {
                console.error("Error creating partner:", error);
                toast.error("Failed to create partner");
            }
        }
    };

    const handleUpdatePartner = async (partnerData) => {
        try {
            const path = `/updatepartnervendor`;
            const URL = process.env.REACT_APP_BE_URL + path;
            const response = await axios.put(URL, partnerData, {
                headers: {
                    Authorization: `Bearer ${storedToken}`,
                    'Content-Type': 'multipart/form-data',
                },
            });
            if (response.status === 200) {
                toast.success("Partner updated successfully");
                fetchPartners();
                setShowEditModal(false);
                setSelectedPartner(null);
            } else {
                handleErrors(response);
            }
        } catch (error) {
            if (error.response) {
                handleErrors(error.response);
            } else {
                console.error("Error updating partner:", error);
                toast.error("Failed to update partner");
            }
        }
    };

    const handleViewPartner = (partner) => {
        setSelectedPartner(partner);
        setShowViewModal(true);
    };

    const handleEditPartner = (partner) => {
        setSelectedPartner(partner);
        setShowEditModal(true);
    };

    const handleExport = () => {
        const selectedFields = filteredPartners.map(item => ({
            PartnerName: item.PartnerName,
            RegistrationNumber: item.RegistrationNumber,
            PartnerType: item.PartnerType,
            ContactNumber: item.ContactNumber,
            Email: item.Email,
            PartnerManager: item.PartnerManager,
        }));
        const ws = utils.json_to_sheet(selectedFields);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Partners');
        const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });
        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const today = new Date();
        const year = today.getFullYear().toString().slice(-2);
        const month = (today.getMonth() + 1).toString().padStart(2, '0');
        const day = today.getDate().toString().padStart(2, '0');
        const formattedDate = `${year}${month}${day}`;
        saveAs(data, `WW360-Partners_Report_${formattedDate}.xlsx`);
    };

    const shareData = {
        title: 'Partners List',
        text: 'Check out the Partners list',
        url: window.location.href
    };

    const handleShare = async () => {
        try {
            await navigator.share(shareData);
        } catch (err) {
            console.error('Error sharing:', err);
        }
    };

    const sendEmail = (email) => {
        const selectedFields = filteredPartners.map(item => ({
            PartnerName: item.PartnerName,
            RegistrationNumber: item.RegistrationNumber,
            PartnerType: item.PartnerType,
            ContactNumber: item.ContactNumber,
            Email: item.Email,
            PartnerManager: item.PartnerManager,
        }));
        const ws = utils.json_to_sheet(selectedFields);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, 'Partners');
        const excelBuffer = write(wb, { bookType: 'xlsx', type: 'array' });

        const data = new Blob([excelBuffer], { type: 'application/octet-stream' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(data);
        const today = new Date();
        const year = today.getFullYear().toString().slice(-2); // Get last two digits of the year
        const month = (today.getMonth() + 1).toString().padStart(2, '0'); // Add leading 0 for month
        const day = today.getDate().toString().padStart(2, '0'); // Add leading 0 for day
        const formattedDate = `${year}${month}${day}`;
        link.download = `WW360-Partners_Report_${formattedDate}.xlsx`;

        // Simulate a click to download the attachment
        link.click();

        // Prepare the mailto link (without attachment)
        const subject = "Partners List";
        const body = 'Please find the attached file.';
        const mailtoLink = `mailto:${email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

        toast.info('The Excel file has been generated and downloaded. Please attach the file manually to your email.', {
            position: toast.POSITION.TOP_CENTER,
        });

        setTimeout(() => {
            window.location.href = mailtoLink;
        }, 3000);

    };

    // Pagination logic
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    const currentRecords = filteredPartners.slice(indexOfFirstRecord, indexOfLastRecord);
    const totalPages = Math.ceil(filteredPartners.length / recordsPerPage);

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    return (
        <div>
            {loading && (
                <div id="preloading">
                    <span className="loader"></span>
                </div>
            )}
            <div className="content-page">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col">
                            <h3 className="title mb-2">Partners Management</h3>
                        </div>
                        <div className="col-auto">
                            <button
                                className="btn btn-primary mt-lg-0 mt-4"
                                onClick={() => setShowAddModal(true)}
                            >
                                <i className="ri-add-line"></i> Add Partner
                            </button>
                        </div>
                    </div>
                    {partners?.length === 0 ? (
                        <div className="container">
                            <div className="row vh-50 justify-content-center align-items-center">
                                <div className="col-auto">
                                    <div className="no-data-wrapper">
                                        <div className="no-data-found">
                                            <i className="ri-file-damage-fill"></i>
                                            <div className="bounce"><i className="ri-search-eye-line"></i></div>
                                            <p>No Data Found</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <div className="row align-items-center justify-content-center mb-4">
                                <div className="col-lg-5 col-md-8 col-sm-10">
                                    <div className="container-fluid row-rounded mb-0">
                                        <div className="row px-2 py-0">
                                            <div className="input-group mb-0">
                                                <input
                                                    type="search"
                                                    className="form-control search-input"
                                                    placeholder="Keyword search..."
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                />
                                                <div className="input-group-append">
                                                    <button className="btn btn-primary" type="button" title="Search" rel="tooltip">
                                                        <i className="ri-menu-search-line"></i>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <small className="form-text text-muted text-center mt-0">
                                        <i className="ri-information-2-fill text-info"></i> Allows: Partner Name - Registration No - Partner Type - Email - Partner Manager
                                    </small>
                                </div>
                            </div>
                            <div className="row">
                                <div className="col-lg-12">
                                    <div className="card mb-4">
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col">
                                                    <h5 className="title mt-1 mb-0">Summary</h5>
                                                </div>
                                                <div className="col-auto">
                                                    <button
                                                        type="button"
                                                        title="Email"
                                                        rel="tooltip"
                                                        className="btn btn-light btn-circle ml-2"
                                                        onClick={() => sendEmail('')}
                                                    >
                                                        <i className="ri-mail-send-fill"></i>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        title="Share"
                                                        rel="tooltip"
                                                        className="btn btn-light btn-circle ml-2"
                                                        onClick={handleShare}
                                                    >
                                                        <i className="ri-share-fill"></i>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        title="Export"
                                                        className="btn btn-light btn-circle ml-2"
                                                        onClick={handleExport}
                                                    >
                                                        <i className="ri-export-fill"></i>
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="row mt-3">
                                                <div className="col-md-12">
                                                    <div className="table-responsive leave-information">
                                                        <table className="table grid">
                                                            <thead className="bg-light">
                                                                <tr>
                                                                    <th>S.No.</th>
                                                                    <th>Partner Name</th>
                                                                    <th>Registration Number</th>
                                                                    <th>Partner Type</th>
                                                                    <th>Contact Number</th>
                                                                    <th>Email</th>
                                                                    <th> In House Partner Manager</th>
                                                                    <th></th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {currentRecords.length > 0 ? (
                                                                    currentRecords.map((partner, index) => (
                                                                        <tr key={partner.id || index}>
                                                                            <td>{indexOfFirstRecord + index + 1}</td>
                                                                            <td>{partner.PartnerName}</td>
                                                                            <td>{partner.RegistrationNumber}</td>
                                                                            <td>
                                                                                <span className="badge badge-info">
                                                                                    {partner.PartnerType}
                                                                                </span>
                                                                            </td>
                                                                            <td>{partner.ContactNumber}</td>
                                                                            
                                                                            <td
                                                                                style={{
                                                                                    maxWidth: '150px',
                                                                                    whiteSpace: 'nowrap',
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis'
                                                                                }}
                                                                                title={partner.Email} // full email on hover
                                                                            >
                                                                                {partner.Email}
                                                                            </td>
                                                                           

                                                                            <td
                                                                                style={{
                                                                                    maxWidth: '150px',
                                                                                    whiteSpace: 'nowrap',
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis'
                                                                                }}
                                                                                title={partner.PartnerManager} // full email on hover
                                                                            >
                                                                                {partner.PartnerManager}
                                                                            </td>
                                                                            <td className="text-center">
                                                                                <span>
                                                                                    <button
                                                                                        className="btn btn-link text-info btn-action"
                                                                                        type="button"
                                                                                        title="View Partner"
                                                                                        rel="tooltip"
                                                                                        onClick={() => handleViewPartner(partner)}
                                                                                    >
                                                                                        <i className="ri-eye-line"></i>
                                                                                    </button>
                                                                                    <button
                                                                                        className="btn btn-link text-warning btn-action"
                                                                                        type="button"
                                                                                        title="Edit Partner"
                                                                                        rel="tooltip"
                                                                                        onClick={() => handleEditPartner(partner)}
                                                                                    >
                                                                                        <i className="ri-edit-line"></i>
                                                                                    </button>
                                                                                </span>
                                                                            </td>
                                                                        </tr>
                                                                    ))
                                                                ) : (
                                                                    <tr>
                                                                        <td colSpan="8" className="text-center">
                                                                            No partners found
                                                                        </td>
                                                                    </tr>
                                                                )}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>
                                                <div className="col-md-12">
                                                    <div className="container-fluid">
                                                        <div className="row border-top pt-3">
                                                            <div className="col-auto">
                                                                <p className="mt-1 mb-0">Records</p>
                                                            </div>
                                                            <div className="col-1 record-drop">
                                                                <select
                                                                    className="form-control"
                                                                    value={recordsPerPage}
                                                                    onChange={(e) => setRecordsPerPage(Number(e.target.value))}
                                                                >
                                                                    <option value="5">5</option>
                                                                    <option value="10">10</option>
                                                                    <option value="15">15</option>
                                                                    <option value="20">20</option>
                                                                    <option value="25">25</option>
                                                                </select>
                                                            </div>
                                                            <div className="col">
                                                                <nav>
                                                                    <ul className="pagination justify-content-center">
                                                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(1)} tabIndex="-1">
                                                                                <i className="ri-arrow-left-double-line"></i>
                                                                            </button>
                                                                        </li>
                                                                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(currentPage - 1)} tabIndex="-1">
                                                                                <i className="ri-arrow-left-s-line"></i>
                                                                            </button>
                                                                        </li>
                                                                        {Array.from({ length: totalPages }, (_, index) => (
                                                                            <li key={index + 1} className={`page-item ${index + 1 === currentPage ? 'active' : ''}`}>
                                                                                <button className="page-link" onClick={() => paginate(index + 1)}>
                                                                                    {index + 1}
                                                                                </button>
                                                                            </li>
                                                                        ))}
                                                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(currentPage + 1)}>
                                                                                <i className="ri-arrow-right-s-line"></i>
                                                                            </button>
                                                                        </li>
                                                                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                                                            <button className="page-link" onClick={() => paginate(totalPages)}>
                                                                                <i className="ri-arrow-right-double-fill"></i>
                                                                            </button>
                                                                        </li>
                                                                    </ul>
                                                                </nav>
                                                            </div>
                                                            <div className="col-auto">
                                                                <p className="mt-1 mb-0">Page Showing {currentPage} of {totalPages}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modals */}
            <AddPartnerModal
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
                onSubmit={handleAddPartner}
            />

            <ViewPartnerModal
                show={showViewModal}
                onHide={() => {
                    setShowViewModal(false);
                    setSelectedPartner(null);
                }}
                partner={selectedPartner}
            />

            <EditPartnerModal
                show={showEditModal}
                onHide={() => {
                    setShowEditModal(false);
                    setSelectedPartner(null);
                }}
                partner={selectedPartner}
                onSubmit={handleUpdatePartner}
            />

            <ToastContainer />
        </div>
    );
};

export default Partners;
